import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Trophy,
    TrendingUp,
    TrendingDown,
    AlertTriangle,
    CheckCircle,
    RefreshCw,
    ArrowRight,
    Building2,
    ShieldAlert,
    ChevronRight,
    HelpCircle,
    BarChart3
} from 'lucide-react';
import { apiClient, SiteRiskLeaderboardEntry } from '../services/api';
import { useScoringConfig } from '../contexts/ScoringConfigContext';
import SiteTrendsTab from './SiteTrendsTab';

interface SitesWidgetProps {
    onViewAll: () => void;
}

/**
 * Compact Site Risk Widget for Dashboard
 * Shows risk distribution summary + top performers + sites needing attention
 * Fixed height, scalable for any number of sites
 */
const SitesWidget: React.FC<SitesWidgetProps> = ({ onViewAll }) => {
    const navigate = useNavigate();
    const { lowMediumBoundary, mediumHighBoundary } = useScoringConfig();
    const [leaderboard, setLeaderboard] = useState<SiteRiskLeaderboardEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'trends'>('overview');

    const handleSiteClick = (siteId: string) => {
        navigate(`/sites?site=${siteId}`);
    };

    useEffect(() => {
        loadLeaderboard();
    }, []);

    const loadLeaderboard = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiClient.getSiteRiskLeaderboard();
            setLeaderboard(response.data);
        } catch (err: any) {
            setError(err.message || 'Failed to load site risk data');
            console.error('Error loading leaderboard:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Calculate risk distribution
    // Only consider sites with drivers for meaningful rankings
    const sitesWithDrivers = leaderboard.filter(s => s.totalDrivers > 0);
    const sitesWithoutDrivers = leaderboard.filter(s => s.totalDrivers === 0);

    // Risk distribution (only sites with drivers)
    const riskDistribution = {
        low: sitesWithDrivers.filter(s => s.riskLevel === 'low').length,
        medium: sitesWithDrivers.filter(s => s.riskLevel === 'medium').length,
        high: sitesWithDrivers.filter(s => s.riskLevel === 'high').length,
        noDrivers: sitesWithoutDrivers.length,
    };

    // Get top 3 performers (lowest scores, must have drivers AND be low risk)
    const topPerformers = sitesWithDrivers
        .filter(s => s.riskLevel === 'low')
        .slice(0, 3);
    const topPerformerIds = new Set(topPerformers.map(s => s.siteId));

    // Get sites needing attention (medium/high risk, sorted by score descending)
    // Exclude sites already shown in top performers to avoid duplication
    // Show more items if there are no top performers (adaptive layout)
    // Limit to 8 for compact 2-column grid (4 rows)
    const maxNeedsAttention = topPerformers.length === 0 ? 8 : 3;
    const needsAttention = sitesWithDrivers
        .filter(s => (s.riskLevel === 'medium' || s.riskLevel === 'high') && !topPerformerIds.has(s.siteId))
        .sort((a, b) => b.totalScore - a.totalScore)
        .slice(0, maxNeedsAttention);

    const getRiskColor = (level: string): string => {
        switch (level) {
            case 'low': return '#69BE28';
            case 'medium': return '#f59e0b';
            case 'high': return '#ef4444';
            default: return '#6b7280';
        }
    };

    // Only show trend icon when there's actual change
    const getTrendIcon = (trend: string) => {
        switch (trend) {
            case 'improving':
                return <span title="Improving"><TrendingDown className="w-3 h-3 text-green-600" /></span>;
            case 'worsening':
                return <span title="Worsening"><TrendingUp className="w-3 h-3 text-red-600" /></span>;
            default:
                return null; // Don't show anything for stable
        }
    };

    const getRankEmoji = (rank: number): string => {
        switch (rank) {
            case 1: return '🥇';
            case 2: return '🥈';
            case 3: return '🥉';
            default: return `${rank}.`;
        }
    };

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-center h-48">
                    <RefreshCw className="w-6 h-6 animate-spin" style={{ color: '#69BE28' }} />
                    <span className="ml-2 text-gray-600">Loading site risk overview...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-center h-48 text-red-600">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    if (leaderboard.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                        <Trophy className="w-5 h-5" style={{ color: '#69BE28' }} />
                        <h3 className="text-lg font-medium text-gray-900">Site Risk Overview</h3>
                    </div>
                </div>
                <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No sites with drivers to compare</p>
                    <p className="text-sm text-gray-400 mt-1">Add drivers to your sites to see risk rankings</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                    <Trophy className="w-5 h-5" style={{ color: '#69BE28' }} />
                    <h3 className="text-lg font-medium text-gray-900">Site Risk Overview</h3>
                    <div className="relative group">
                        <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                        <div className="absolute left-0 top-6 w-80 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                            <p className="font-semibold text-sm mb-2">How Site Scores Work</p>
                            <p className="text-gray-300 mb-3">
                                Site Score = <span className="text-white font-medium">Average of all driver risk scores</span>. Lower is better.
                            </p>

                            <p className="text-gray-400 uppercase text-[10px] tracking-wide mb-2">Each driver scores</p>
                            <div className="space-y-1.5 mb-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-300">Recently checked</span>
                                    <span className="text-gray-500">Their licence check score</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-300">Never checked</span>
                                    <span className="text-gray-500">50 points (unknown risk)</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-300">Overdue for recheck</span>
                                    <span className="text-gray-500">Score + 15 points</span>
                                </div>
                            </div>

                            <p className="text-gray-400 text-[10px] mb-3">
                                Driver licence check scores factor in: penalty points, endorsements, disqualifications, licence status, serious offence codes, and more.
                            </p>

                            <div className="pt-2 border-t border-gray-700">
                                <p className="text-gray-400 uppercase text-[10px] tracking-wide mb-1.5">Risk levels (aligned with driver scoring)</p>
                                <div className="flex items-center space-x-4">
                                    <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>Low &lt;{lowMediumBoundary}</span>
                                    <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-amber-500 mr-1.5"></span>Medium {lowMediumBoundary}-{mediumHighBoundary - 1}</span>
                                    <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></span>High {mediumHighBoundary}+</span>
                                </div>
                            </div>

                            <div className="absolute -top-1.5 left-3 w-3 h-3 bg-gray-900 rotate-45"></div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={loadLeaderboard}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onViewAll}
                        className="flex items-center text-sm font-medium hover:opacity-80 transition-opacity"
                        style={{ color: '#69BE28' }}
                    >
                        View All {leaderboard.length}
                        <ArrowRight className="w-4 h-4 ml-1" />
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-4">
                <button
                    onClick={() => setActiveTab('overview')}
                    className={`flex items-center px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'overview'
                            ? 'border-green-500 text-green-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    <Trophy className="w-4 h-4 mr-1.5" />
                    Overview
                </button>
                <button
                    onClick={() => setActiveTab('trends')}
                    className={`flex items-center px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'trends'
                            ? 'border-green-500 text-green-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    <BarChart3 className="w-4 h-4 mr-1.5" />
                    Trends
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' ? (
                <>
            {/* Risk Distribution Summary */}
            <div className="flex items-center justify-center space-x-4 mb-6 py-3 bg-gray-50 rounded-lg flex-wrap gap-y-2">
                <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-gray-700">{riskDistribution.low} Low</span>
                </div>
                <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-medium text-gray-700">{riskDistribution.medium} Medium</span>
                </div>
                <div className="flex items-center space-x-2">
                    <ShieldAlert className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-medium text-gray-700">{riskDistribution.high} High</span>
                </div>
                {riskDistribution.noDrivers > 0 && (
                    <div className="flex items-center space-x-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-500">{riskDistribution.noDrivers} Empty</span>
                    </div>
                )}
            </div>

            {/* Adaptive Layout: Two columns with top/bottom, or single grid if no top performers */}
            {topPerformers.length > 0 ? (
                // Two Section Layout - Top Performers + Needs Attention
                <div className="grid grid-cols-2 gap-6">
                    {/* Top Performers */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-600 mb-3 flex items-center">
                            <Trophy className="w-4 h-4 mr-1 text-yellow-500" />
                            Top Performers
                        </h4>
                        <div className="space-y-2">
                            {topPerformers.map((site) => (
                                <button
                                    key={site.siteId}
                                    onClick={() => handleSiteClick(site.siteId)}
                                    className="w-full flex items-center justify-between px-3 py-2 bg-green-50 rounded-lg hover:bg-green-100 transition-colors cursor-pointer text-left"
                                >
                                    <div className="flex items-center min-w-0">
                                        <span className="text-sm mr-2 flex-shrink-0">{getRankEmoji(site.rank)}</span>
                                        <span className="text-sm font-medium text-gray-800 truncate">{site.siteName}</span>
                                    </div>
                                    <div className="flex items-center flex-shrink-0 ml-2">
                                        {getTrendIcon(site.trend)}
                                        <span
                                            className="ml-1 px-2 py-0.5 rounded text-xs font-bold text-white"
                                            style={{ backgroundColor: getRiskColor(site.riskLevel) }}
                                        >
                                            {site.totalScore.toFixed(0)}
                                        </span>
                                        <ChevronRight className="w-4 h-4 ml-1 text-gray-400" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Needs Attention */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-600 mb-3 flex items-center">
                            <AlertTriangle className="w-4 h-4 mr-1 text-amber-500" />
                            Needs Attention
                        </h4>
                        <div className="space-y-2">
                            {needsAttention.length > 0 ? (
                                needsAttention.map((site, index) => (
                                    <button
                                        key={site.siteId}
                                        onClick={() => handleSiteClick(site.siteId)}
                                        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-left"
                                    >
                                        <div className="flex items-center min-w-0">
                                            <span className="text-xs font-medium text-gray-400 mr-2">{index + 1}</span>
                                            <span className="text-sm font-medium text-gray-800 truncate">{site.siteName}</span>
                                        </div>
                                        <div className="flex items-center flex-shrink-0 ml-2">
                                            {getTrendIcon(site.trend)}
                                            <span
                                                className="ml-1 px-2 py-0.5 rounded text-xs font-bold text-white"
                                                style={{ backgroundColor: getRiskColor(site.riskLevel) }}
                                            >
                                                {site.totalScore.toFixed(0)}
                                            </span>
                                            <ChevronRight className="w-4 h-4 ml-1 text-gray-400" />
                                        </div>
                                    </button>
                                ))
                            ) : (
                                <div className="flex items-center justify-center py-6 bg-green-50 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                                    <span className="text-sm text-green-700 font-medium">All sites compliant!</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                // Compact 2-column grid - shows top worst sites only
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-600">
                            Sites Needing Attention
                        </h4>
                        <span className="text-xs text-gray-400">
                            Aim for &lt;{lowMediumBoundary}
                        </span>
                    </div>
                    {needsAttention.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3">
                            {needsAttention.map((site, index) => (
                                <button
                                    key={site.siteId}
                                    onClick={() => handleSiteClick(site.siteId)}
                                    className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-left"
                                >
                                    <div className="flex items-center min-w-0">
                                        <span className="text-xs font-medium text-gray-400 mr-2">{index + 1}</span>
                                        <span className="text-sm font-medium text-gray-800 truncate">{site.siteName}</span>
                                    </div>
                                    <div className="flex items-center flex-shrink-0 ml-2">
                                        {getTrendIcon(site.trend)}
                                        <span
                                            className="ml-1 px-2 py-0.5 rounded text-xs font-bold text-white"
                                            style={{ backgroundColor: getRiskColor(site.riskLevel) }}
                                        >
                                            {site.totalScore.toFixed(0)}
                                        </span>
                                        <ChevronRight className="w-4 h-4 ml-1 text-gray-400" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center py-6 bg-green-50 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                            <span className="text-sm text-green-700 font-medium">All sites compliant!</span>
                        </div>
                    )}
                </div>
            )}

            {/* Footer Legend */}
            <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                    <span>Score = Avg driver risk</span>
                    <span>•</span>
                    <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: '#69BE28' }} />
                        <span>&lt;{lowMediumBoundary}</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: '#f59e0b' }} />
                        <span>{lowMediumBoundary}-{mediumHighBoundary - 1}</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: '#ef4444' }} />
                        <span>{mediumHighBoundary}+</span>
                    </div>
                </div>
            </div>
                </>
            ) : (
                <SiteTrendsTab />
            )}
        </div>
    );
};

export default SitesWidget;
